import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { ensurePushSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush, getPushSupport } from "@/lib/push";

export default function PushSettings() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  useEffect(() => {
    (async () => {
      const ok = await getPushSupport();
      setSupported(ok);
      if (ok) {
        const status = await ensurePushSubscription();
        setSubscribed(!!status.subscribed);
      }
    })();
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPerm("granted");
      toast.success("Push-notifikationer aktiveret");
    } catch (e) {
      toast.error(e.message || "Kunne ikke aktivere");
    } finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Push deaktiveret");
    } catch (e) { toast.error("Kunne ikke deaktivere"); }
    finally { setLoading(false); }
  };

  const test = async () => {
    setLoading(true);
    try {
      await sendTestPush();
      toast.success("Test sendt, tjek din notifikations-tray");
    } catch { toast.error("Test fejlede"); }
    finally { setLoading(false); }
  };

  if (!supported) {
    return (
      <Card className="border-muted">
        <CardContent className="py-4 text-sm text-muted-foreground">
          Browser-push understøttes ikke i denne browser/enhed.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${subscribed ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
            {subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div>
            <p className="font-semibold text-sm">Push-notifikationer</p>
            <p className="text-xs text-muted-foreground">
              {subscribed ? "Du modtager push selv når appen er lukket" : "Få besked om nye bud, beskeder og opdateringer"}
            </p>
            {perm === "denied" && (
              <p className="text-[11px] text-destructive mt-0.5">Tilladelse blokeret, ændr i browser-indstillinger</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {subscribed ? (
            <>
              <Button size="sm" variant="outline" onClick={test} disabled={loading} className="gap-1.5" data-testid="push-test-btn">
                <Send className="w-3.5 h-3.5" /> Test
              </Button>
              <Button size="sm" variant="outline" onClick={disable} disabled={loading} data-testid="push-disable-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Slå fra"}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={enable} disabled={loading || perm === "denied"} className="gap-1.5" data-testid="push-enable-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              Aktivér
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
